import Chart from 'chart.js/auto';

const GeolocationsChartHook = {
  chart: null,

  mounted() {
    console.log('GeolocationsChart hook mounted');
    this.initChart();
  },

  updated() {
    console.log('GeolocationsChart hook updated');
    this.updateChart();
  },

  destroyed() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },

  initChart() {
    try {
      if (!this.el.dataset.chartData) {
        console.error('No chart data attribute found');
        return;
      }

      const ctx = this.el.getContext('2d');
      const chartData = JSON.parse(this.el.dataset.chartData);
      const chartType = this.el.dataset.chartType || 'daily';

      console.log('Geolocations Chart data:', chartData);
      console.log('Chart type:', chartType);
      console.log('Chart data keys:', Object.keys(chartData));
      console.log('Labels:', chartData.labels);
      console.log('Datasets:', chartData.datasets);

      if (!chartData.labels || !chartData.datasets) {
        console.error('Chart data missing labels or datasets:', chartData);
        return;
      }

      // Calculate max value from original data (before offset) for proper Y-axis scaling
      const allOriginalValues = chartData.datasets.flatMap(d => d.data.filter(v => v != null && v !== 0));
      const maxOriginalValue = allOriginalValues.length > 0 ? Math.max(...allOriginalValues) : 1;
      const stepSize = this.calculateStepSize(maxOriginalValue);

      // Add small offset to overlapping lines for better visibility
      const processedData = this.addOffsetToOverlappingLines(chartData);

      // Store reference to hook instance for use in callbacks
      const hookInstance = this;

      this.chart = new Chart(ctx, {
        type: 'line',
        data: processedData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                color: '#666',
                font: { size: 12 },
              },
              onClick: (e, legendItem, legend) => {
                // Get the chart instance
                const chart = legend.chart;
                const index = legendItem.datasetIndex;
                const meta = chart.getDatasetMeta(index);

                // Toggle visibility
                meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;

                // Recalculate scale based on visible datasets
                hookInstance.recalculateYAxisScale(chart);

                // Update the chart
                chart.update();
              },
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  // Show original value (remove offset for display)
                  // Calculate offset the same way as in addOffsetToOverlappingLines
                  const allValues = context.chart.data.datasets.flatMap(d => d.data.filter(v => v != null && v !== 0));
                  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 1;
                  const baseOffset = maxValue * 0.009;
                  const offset = context.datasetIndex * baseOffset;
                  const originalValue = context.parsed.y - offset;
                  if (context.parsed.y !== null) {
                    // If the displayed value is 0 or very close to 0, show 0
                    const rounded = Math.round(originalValue);
                    label += rounded <= 0 ? 0 : rounded;
                  }
                  return label;
                }
              }
            },
          },
          scales: {
            x: {
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
                display: true,
              },
              ticks: {
                color: '#666',
                maxRotation: chartType === 'daily' ? 45 : 0,
                minRotation: 0
              },
            },
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
                borderDash: [5, 5],
              },
              ticks: {
                color: '#666',
                stepSize: stepSize,
                precision: 0,
                callback: function(value) {
                  // Show whole numbers without formatting (no "k" suffix)
                  if (Number.isInteger(value) || Math.abs(value - Math.round(value)) < 0.001) {
                    return Math.round(value);
                  }
                  return '';
                }
              },
              // Ensure proper scaling for small values
              suggestedMax: maxOriginalValue > 0 ? Math.ceil((maxOriginalValue * 1.2) / stepSize) * stepSize : stepSize,
            },
          },
          elements: {
            point: {
              radius: processedData.labels && processedData.labels.length === 1 ? 6 : 5,
              hoverRadius: 8,
              backgroundColor: function(context) {
                return context.dataset.borderColor;
              },
              borderWidth: 2,
              borderColor: function(context) {
                return context.dataset.borderColor;
              }
            },
            line: {
              tension: 0.3,
              borderWidth: 2.5
            }
          }
        },
      });

      console.log('Geolocations Chart initialized successfully');
    } catch (error) {
      console.error('Error initializing geolocations chart:', error);
    }
  },

  updateChart() {
    if (!this.chart) {
      this.initChart();
      return;
    }

    try {
      const newData = JSON.parse(this.el.dataset.chartData);

      // Calculate max value from original data (before offset) for proper Y-axis scaling
      const allOriginalValues = newData.datasets.flatMap(d => d.data.filter(v => v != null && v !== 0));
      const maxOriginalValue = allOriginalValues.length > 0 ? Math.max(...allOriginalValues) : 1;
      const stepSize = this.calculateStepSize(maxOriginalValue);

      console.log('Step size:', stepSize);
      console.log('Max original value:', maxOriginalValue);
      // Add small offset to overlapping lines for better visibility
      const processedData = this.addOffsetToOverlappingLines(newData);

      this.chart.data = processedData;
      this.chart.options.scales.y.ticks.stepSize = stepSize;
      // Update suggestedMax for proper scaling with small values
      this.chart.options.scales.y.suggestedMax = maxOriginalValue > 0 ? Math.ceil((maxOriginalValue * 1.2) / stepSize) * stepSize : stepSize;
      this.chart.update();

    } catch (error) {
      console.error('Error updating geolocations chart:', error);
    }
  },

  calculateStepSize(maxValue) {
    // For very small values (0-5), use step of 1
    if (maxValue <= 5) {
      return 1;
    }
    // For small values (6-10), use step of 2
    if (maxValue <= 10) {
      return 2;
    }
    // For small values (11-20), use step of 5
    if (maxValue <= 20) {
      return 5;
    }
    // For small values (21-50), use step of 10
    if (maxValue <= 50) {
      return 10;
    }
    // For medium values (51-250), use step of 25
    if (maxValue <= 250) {
      return 25;
    }
    // For larger values (251-1000), use step of 50
    if (maxValue <= 1000) {
      return 50;
    }
    // For values 1000 and above, use step of 500
    if (maxValue <= 5000) {
      return 500;
    }
    // For very large values, use step of 1000
    if (maxValue <= 10000) {
      return 1000;
    }
    // For extremely large values, use step of 5000
    return 5000;
  },

  recalculateYAxisScale(chart) {
    // Get indices of visible datasets
    const visibleDatasetIndices = chart.data.datasets
      .map((ds, idx) => {
        const meta = chart.getDatasetMeta(idx);
        return (!meta.hidden && !ds.hidden) ? idx : null;
      })
      .filter(idx => idx !== null);

    if (visibleDatasetIndices.length === 0) {
      // If no datasets are visible, use default scale
      const stepSize = 1;
      chart.options.scales.y.ticks.stepSize = stepSize;
      chart.options.scales.y.suggestedMax = stepSize;
      return;
    }

    // Get original data from dataset attribute to calculate real max from visible datasets only
    try {
      const originalData = JSON.parse(this.el.dataset.chartData);
      const visibleOriginalDatasets = visibleDatasetIndices.map(idx => originalData.datasets[idx]);
      const allVisibleOriginalValues = visibleOriginalDatasets
        .flatMap(d => d ? d.data.filter(v => v != null && v !== 0) : []);

      const maxOriginalValue = allVisibleOriginalValues.length > 0 ? Math.max(...allVisibleOriginalValues) : 1;
      const stepSize = this.calculateStepSize(maxOriginalValue);

      chart.options.scales.y.ticks.stepSize = stepSize;
      chart.options.scales.y.suggestedMax = maxOriginalValue > 0
        ? Math.ceil((maxOriginalValue * 1.2) / stepSize) * stepSize
        : stepSize;
    } catch (error) {
      console.error('Error recalculating Y-axis scale:', error);
    }
  },

  addOffsetToOverlappingLines(chartData) {
    if (!chartData.datasets || chartData.datasets.length === 0) {
      return chartData;
    }

    // Calculate max value to make offset relative to chart scale
    const allValues = chartData.datasets.flatMap(d => d.data.filter(v => v != null && v !== 0));
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 1;

    // Very small offset (about 0.5% of max value, roughly 2-3mm visually)
    const baseOffset = maxValue * 0.009;

    const processedData = {
      labels: chartData.labels,
      datasets: chartData.datasets.map((dataset, datasetIndex) => {
        // Add tiny offset based on dataset index to separate overlapping lines
        // Offset is very small (0.5% of max value) - just enough to see different colors
        const offset = datasetIndex * baseOffset;

        const processedData = dataset.data.map(value => {
          if (value === null || value === undefined) {
            return value;
          }
          // Keep zero values as zero - don't add offset to them
          if (value === 0) {
            return 0;
          }
          return value + offset;
        });

        return {
          ...dataset,
          data: processedData,
          // Add different point styles for better distinction
          pointStyle: datasetIndex === 0 ? 'circle' : datasetIndex === 1 ? 'rect' : 'triangle',
          pointRadius: 5,
          pointHoverRadius: 8,
        };
      })
    };

    return processedData;
  },
};

export default GeolocationsChartHook;
